import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import SupportDialogFixed from '../components/SupportDialog'; 
import MobilePricingTable from './MobilePricingTable';
import { useAuth } from '../contexts/AuthContext';
import { 
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box, 
  Card,
  CardContent,
  Chip,
  Container, 
  Grid,
  Typography, 
  Button, 
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import StarsRoundedIcon from '@mui/icons-material/StarsRounded';

import NavBar from '../components/common/NavBar';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { PRICING_PLANS, ADD_ONS } from '../config/Plans';

const trustSignals = [
  { title: 'No hidden fees', icon: LockOutlinedIcon },
  { title: 'Cancel anytime', icon: AutoAwesomeOutlinedIcon },
  { title: 'Built for weekly progress', icon: InsightsRoundedIcon },
];

const faqItems = [
  {
    question: 'Is there a free trial available?',
    answer: 'Yes, we offer a 14-day free trial for all our pro plans. No credit card is required to start your trial.',
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards including Visa, Mastercard, and American Express.',
  },
  {
    question: 'Where is my credit card information stored?',
    answer: 'Your credit card information is never stored on our servers. All payment details are securely handled by Stripe, which is PCI-DSS compliant.',
  },
  {
    question: 'Can I change plans later?',
    answer: 'Yes, you can upgrade or downgrade your plan at any time. Changes apply at the start of your next billing cycle.',
  },
  {
    question: 'How do I cancel my subscription?',
    answer: 'You can cancel your subscription at any time from your account settings. Your subscription remains active until the end of the current billing period.',
  },
];

const Pricing = () => {
  const [activeTab, setActiveTab] = useState('Student');
  const [referralCode, setReferralCode] = useState(null);
  const [referralDiscount, setReferralDiscount] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isVerySmallMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const { user } = useAuth();
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Function to get cookie value
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  // Check for referral code on component mount
  useEffect(() => {
    const refCode = getCookie('ref_code');
    if (refCode) {
      setReferralCode(refCode);
      // Fetch discount details from backend
      fetchReferralDiscount(refCode);
      
      // Clean up sessionStorage after reading referral code
      sessionStorage.removeItem('inviteReferralCode');
      console.log('Referral code read from cookie:', refCode);
    }
  }, []);

  // Function to fetch referral discount details
  const fetchReferralDiscount = async (code) => {
    try {
      const response = await fetch(`/api/validate-code/${code}/`);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.valid) {
          const discountInfo = {
            type: 'referral',
            code: code,
            discountType: data.discount_type,
            discountAmount: data.discount_amount,
            isValid: true
          };
          setReferralDiscount(discountInfo);
        } else {
          setReferralDiscount({ type: 'referral', code: code, isValid: false });
        }
      } else {
        setReferralDiscount({ type: 'referral', code: code, isValid: false });
      }
    } catch (error) {
      console.error('Error fetching referral discount:', error);
      // Set basic referral info even if discount fetch fails
      setReferralDiscount({ type: 'referral', code: code, isValid: false });
    }
  };
  
  const navigate = useNavigate();
  const [supportOpen, setSupportOpen] = useState(false);
  const supportDialogRef = useRef();
  const slug = (str) =>
    str.toLowerCase().replace(/\s+/g, '-');           // "Teacher assisted" → "teacher-assisted"

  // Function to calculate discounted price
  const calculateDiscountedPrice = (originalPrice, discountType, discountAmount) => {
    if (!discountType || !discountAmount || originalPrice === 'Free' || originalPrice === 'Custom') {
      return originalPrice;
    }

    // Convert discount amount to number if it's a string
    const numericDiscountAmount = parseFloat(discountAmount);
    if (isNaN(numericDiscountAmount)) {
      return originalPrice;
    }

    // Extract numeric value from price string (e.g., "A$15" → 15)
    const priceMatch = originalPrice.match(/A\$(\d+(?:\.\d+)?)/);
    if (!priceMatch) {
      return originalPrice;
    }

    const numericPrice = parseFloat(priceMatch[1]);
    
    let discountedPrice;

    if (discountType === 'PERCENT') {
      // Percentage discount
      discountedPrice = numericPrice * (1 - numericDiscountAmount / 100);
    } else if (discountType === 'FIXED') {
      // Fixed amount discount
      discountedPrice = Math.max(0, numericPrice - numericDiscountAmount);
    } else {
      return originalPrice;
    }

    // Format back to currency string
    return `A$${discountedPrice.toFixed(2)}`;
  };

  // Function to get display price with discount
  const getDisplayPrice = (plan) => {
    if (!referralDiscount?.isValid || !referralDiscount?.discountType || !referralDiscount?.discountAmount) {
      return plan.price;
    }

    const discountedPrice = calculateDiscountedPrice(
      plan.price, 
      referralDiscount.discountType, 
      referralDiscount.discountAmount
    );

    return {
      original: plan.price,
      discounted: discountedPrice,
      hasDiscount: discountedPrice !== plan.price
    };
  };

    const handleChoosePlan = (planTitle) => {
    const selectedPlan = PRICING_PLANS[activeTab].find(p => p.title === planTitle);
    if (!selectedPlan) return;

    if (selectedPlan.title === 'School') {
      setSupportOpen(true);
      return;
    }

    // Special handling for Champions
    if (activeTab === 'Champions') {
      navigate('/register', { 
        state: { 
          role: 'champion',
          planCode: 'champion_free',
          plan: 'champion',
          planTitle: 'Champion',
          planPrice: 'Free',
          originalPrice: 'Free',
          planPeriod: '',
          planDuration: 'onetime',
          referralCode: referralCode,
          referralDiscount: referralDiscount
        } 
      });
      return;
    }
  
    const role = activeTab.toLowerCase();
    const code = selectedPlan.code; // e.g. student_starter
    const plan = planTitle.toLowerCase().replace(/\s+/g, '-'); // e.g. teacher-assisted
    
    // Get display price with discount
    const displayPrice = getDisplayPrice(selectedPlan);
    const finalPrice = typeof displayPrice === 'string' ? displayPrice : displayPrice.discounted;
    
    // Pass additional plan information for display
    navigate('/register', { 
      state: { 
        role, 
        planCode: code, 
        plan,
        planTitle: selectedPlan.title,
        planPrice: finalPrice,
        originalPrice: selectedPlan.price,
        planPeriod: selectedPlan.period,
        planDuration: selectedPlan.duration,
        referralCode: referralCode, // Pass referral code if available
        referralDiscount: referralDiscount // Pass discount details
      } 
    });
  };


  const allFeatures = useMemo(() => {
    return Array.from(new Set(
      PRICING_PLANS[activeTab].flatMap(plan =>
        Array.isArray(plan.features)
          ? plan.features
          : Object.keys(plan.features)
      )
    ));
  }, [activeTab]);

  const sectionCardSx = {
    borderRadius: 6,
    border: '1px solid rgba(148, 163, 184, 0.18)',
    backgroundColor: '#ffffff',
    boxShadow: '0 24px 60px rgba(15, 23, 42, 0.08)',
  };

  const heroCardSx = {
    ...sectionCardSx,
    p: { xs: 3, sm: 4, md: 5 },
    background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)',
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column', 
      backgroundColor: '#f7f8fc',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: -120,
        right: -120,
        width: 340,
        height: 340,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99,102,241,0.2) 0%, rgba(99,102,241,0) 72%)',
        zIndex: 0,
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 360,
        left: -100,
        width: 260,
        height: 260,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(20,184,166,0.14) 0%, rgba(20,184,166,0) 72%)',
        zIndex: 0,
      },
    }}>
      <Box sx={{ position: 'relative', zIndex: 10 }}>
        <NavBar />
      </Box>
      
      <Helmet>
        <title>EduAssess - Pricing</title>
        <link rel="preconnect" href="https://fonts.gstatic.com/" crossOrigin="" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700;900&family=Noto+Sans:wght@400;500;700;900&display=swap"
        />
      </Helmet>

      <Container maxWidth="lg" sx={{ 
        py: { xs: 4, sm: 5, md: 7, lg: 9 }, 
        flex: 1, 
        position: 'relative', 
        zIndex: 1,
        px: { xs: 1, sm: 2, md: 3, lg: 4 }
      }}>
        <Box 
          textAlign="center" 
          mb={{ xs: 4, sm: 6, md: 7 }}
          sx={heroCardSx}
        >
          <Chip
            icon={<SchoolRoundedIcon sx={{ color: '#2563eb !important' }} />}
            label="Flexible plans for every stage"
            sx={{ mb: 2, pl: 1, borderRadius: 999, backgroundColor: '#eef4ff', color: '#1e3a8a', fontWeight: 600 }}
          />
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontWeight: 800,
              mb: 2,
              color: '#0f172a',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.2rem' },
              lineHeight: 1.08,
              letterSpacing: '-0.03em'
            }}
          >
            Simple, transparent pricing
          </Typography>
          <Typography 
            color="text.secondary" 
            maxWidth="md" 
            mx="auto"
            sx={{ 
              fontSize: { xs: '1rem', md: '1.12rem' },
              lineHeight: 1.7,
              mb: 4,
              px: { xs: 1, sm: 0 },
              color: '#64748b'
            }}
          >
            Choose the plan that fits your learning journey.
          </Typography>

          {/* Referral Code Indicator */}
          {referralCode && (
            <Box 
              sx={{ 
                background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
                color: 'white',
                borderRadius: 4,
                p: 2.2,
                mb: 4,
                maxWidth: 640,
                mx: 'auto',
                boxShadow: '0 18px 40px rgba(34, 197, 94, 0.24)'
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                Special pricing available with your referral code: <strong>{referralCode}</strong>
              </Typography>
              {referralDiscount?.isValid && referralDiscount?.discountType && referralDiscount?.discountAmount && (
                <Typography variant="body2" sx={{ opacity: 0.92 }}>
                  {referralDiscount.discountType === 'PERCENT' 
                    ? `Save ${referralDiscount.discountAmount}% on paid plans.`
                    : `Save A$${referralDiscount.discountAmount} on paid plans.`
                  }
                </Typography>
              )}
            </Box>
          )}

          {/* Role Selection Tabs - Direct on base card */}
          <Tabs
             value={activeTab}
             onChange={handleTabChange}
             centered={!isMobile}
             variant={isMobile ? "scrollable" : "standard"}
             scrollButtons={isMobile ? "auto" : false}
             allowScrollButtonsMobile={true}
             TabIndicatorProps={{ style: { display: 'none' } }}
             className="no-background-tabs"
             sx={{
               mt: 4,
               minHeight: { xs: 36, sm: 40, md: 48 },
               px: { xs: 2, sm: 1 },
               background: 'transparent !important',
               backgroundColor: 'transparent !important',
               backgroundImage: 'none !important',
               boxShadow: 'none !important',
               border: 'none !important',
               borderRadius: '0 !important',
               '&.no-background-tabs': {
                 background: 'transparent !important',
                 backgroundColor: 'transparent !important',
                 backgroundImage: 'none !important',
                 boxShadow: 'none !important',
                 border: 'none !important',
                 borderRadius: '0 !important'
               },
               '& .MuiTabs-root': {
                 background: 'transparent !important',
                 backgroundColor: 'transparent !important',
                 backgroundImage: 'none !important',
                 boxShadow: 'none !important',
                 border: 'none !important',
                 borderRadius: '0 !important'
               },
               '& .MuiTabs-scroller': {
                 background: 'transparent !important',
                 backgroundColor: 'transparent !important',
                 backgroundImage: 'none !important',
                 boxShadow: 'none !important',
                 border: 'none !important',
                 borderRadius: '0 !important'
               },
               '& .MuiTabs-flexContainer': {
                 background: 'transparent !important',
                 backgroundColor: 'transparent !important',
                 backgroundImage: 'none !important',
                 boxShadow: 'none !important',
                 border: 'none !important',
                 borderRadius: '0 !important',
                 gap: isVerySmallMobile ? 0.5 : 1
               },
               '& .MuiTabs-scrollButtons': {
                 color: 'primary.main',
                 '&.Mui-disabled': {
                   opacity: 0.3
                 }
               },
               '& .MuiTabs-scroller': {
                 overflow: 'visible'
               }
             }}
           >
              <Tab
                 label="Student"
                 value="Student"
                 disableRipple
                 sx={{
                   textTransform: 'none',
                   minWidth: { xs: 90, sm: 100, md: 110 },
                   maxWidth: { xs: 90, sm: 100, md: 110 },
                   color: 'text.secondary',
                   fontWeight: '600',
                   borderRadius: '999px',
                   fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                   px: { xs: 1, sm: 1.25, md: 1.5 },
                   py: { xs: 0.35, sm: 0.5, md: 0.75 },
                   transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                   backgroundColor: 'rgba(255, 255, 255, 0.1)',
                   border: 'none',
                   boxShadow: 'none',
                   '&.Mui-selected': {
                     backgroundColor: 'rgba(255, 255, 255, 0.9)',
                     color: 'primary.main',
                     boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                     transform: 'none'
                   },
                   '&:hover': {
                     backgroundColor: 'rgba(255, 255, 255, 0.2)',
                     color: 'primary.main'
                   }
                 }}
               />
              <Tab
                 label="Teacher"
                 value="Teacher"
                 disableRipple
                 sx={{
                   textTransform: 'none',
                   minWidth: { xs: 90, sm: 100, md: 110 },
                   maxWidth: { xs: 90, sm: 100, md: 110 },
                   color: 'text.secondary',
                   fontWeight: '600',
                   borderRadius: '999px',
                   fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                   px: { xs: 1, sm: 1.25, md: 1.5 },
                   py: { xs: 0.35, sm: 0.5, md: 0.75 },
                   transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                   backgroundColor: 'rgba(255, 255, 255, 0.1)',
                   border: 'none',
                   boxShadow: 'none',
                   '&.Mui-selected': {
                     backgroundColor: 'rgba(255, 255, 255, 0.9)',
                     color: 'primary.main',
                     boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                     transform: 'none'
                   },
                   '&:hover': {
                     backgroundColor: 'rgba(255, 255, 255, 0.2)',
                     color: 'primary.main'
                   }
                 }}
               />
              <Tab
                 label="Parent"
                 value="Parent"
                 disableRipple
                 sx={{
                   textTransform: 'none',
                   minWidth: { xs: 90, sm: 100, md: 110 },
                   maxWidth: { xs: 90, sm: 100, md: 110 },
                   color: 'text.secondary',
                   fontWeight: '600',
                   borderRadius: '999px',
                   fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                   px: { xs: 1, sm: 1.25, md: 1.5 },
                   py: { xs: 0.35, sm: 0.5, md: 0.75 },
                   transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                   backgroundColor: 'rgba(255, 255, 255, 0.1)',
                   border: 'none',
                   boxShadow: 'none',
                   '&.Mui-selected': {
                     backgroundColor: 'rgba(255, 255, 255, 0.9)',
                     color: 'primary.main',
                     boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                     transform: 'none'
                   },
                   '&:hover': {
                     backgroundColor: 'rgba(255, 255, 255, 0.2)',
                     color: 'primary.main'
                   }
                 }}
               />
              <Tab
                 label="Champions"
                 value="Champions"
                 disableRipple
                 sx={{
                   textTransform: 'none',
                   minWidth: { xs: 90, sm: 100, md: 110 },
                   maxWidth: { xs: 90, sm: 100, md: 110 },
                   color: 'text.secondary',
                   fontWeight: '600',
                   borderRadius: '999px',
                   fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                   px: { xs: 1, sm: 1.25, md: 1.5 },
                   py: { xs: 0.35, sm: 0.5, md: 0.75 },
                   transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                   backgroundColor: 'rgba(255, 255, 255, 0.1)',
                   border: 'none',
                   boxShadow: 'none',
                   '&.Mui-selected': {
                     backgroundColor: 'rgba(255, 255, 255, 0.9)',
                     color: 'primary.main',
                     boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                     transform: 'none'
                   },
                   '&:hover': {
                     backgroundColor: 'rgba(255, 255, 255, 0.2)',
                     color: 'primary.main'
                   }
                 }}
               />
            </Tabs>
        </Box>

        {/* Champions Special Section */}
        {activeTab === 'Champions' ? (
          <Box 
            sx={{ 
              mb: { xs: 4, sm: 6, md: 8 },
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(20px)',
              borderRadius: 4,
              p: { xs: 3, sm: 4, md: 6 },
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 4px 20px rgba(255, 255, 255, 0.15)'
            }}
          >
            <Box textAlign="center" mb={4}>
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                sx={{ 
                  fontWeight: '900',
                  background: 'linear-gradient(45deg, #4caf50, #66bb6a)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2
                }}
              >
                🏆 Become a Champion
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ mb: 3, lineHeight: 1.6 }}
              >
                Help students succeed while building your network and earning rewards
              </Typography>
            </Box>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: { xs: 3, md: 4 },
              mb: 4
            }}>
              <Box sx={{
                p: 3,
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.8)',
                border: '1px solid rgba(33, 150, 243, 0.2)',
                textAlign: 'center'
              }}>
                <Typography variant="h5" sx={{ color: 'info.main', mb: 2, fontWeight: 'bold' }}>
                  🌟 Free Access
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Enjoy platform access without any subscription fees
                </Typography>
                <Typography variant="subtitle2" sx={{ color: 'info.main', fontWeight: '600' }}>
                  No monthly payments required
                </Typography>
              </Box>

              <Box sx={{
                p: 3,
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.8)',
                border: '1px solid rgba(76, 175, 80, 0.2)',
                textAlign: 'center'
              }}>
                <Typography variant="h5" sx={{ color: 'success.main', mb: 2, fontWeight: 'bold' }}>
                  💰 Earn Rewards
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Get rewarded for every successful referral. The more students and teachers you help join, the more you earn.
                </Typography>
                <Typography variant="subtitle2" sx={{ color: 'success.main', fontWeight: '600' }}>
                  Build your income while helping others
                </Typography>
              </Box>
            </Box>

            <Box sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(33, 150, 243, 0.1))',
              border: '1px solid rgba(76, 175, 80, 0.3)',
              textAlign: 'center'
            }}>
              <Typography variant="h5" sx={{ 
                mb: 3, 
                fontWeight: 'bold', 
                color: 'primary.main'
              }}>
                How It Works
              </Typography>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: { xs: 2, sm: 3 },
                mb: 4
              }}>
                <Box>
                  <Typography variant="h6" sx={{ 
                    color: 'success.main', 
                    mb: 1, 
                    fontWeight: 'bold'
                  }}>
                    1. Join
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Sign up as a Champion and get your unique referral link
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ 
                    color: 'info.main', 
                    mb: 1, 
                    fontWeight: 'bold'
                  }}>
                    2. Invite
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Share your link with students, teachers, and parents
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ 
                    color: 'warning.main', 
                    mb: 1, 
                    fontWeight: 'bold'
                  }}>
                    3. Earn
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Get rewarded for every successful referral
                  </Typography>
                </Box>
              </Box>
              
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  setSupportOpen(true);
                  // Pre-fill the support ticket with Champions Program details
                  setTimeout(() => {
                    if (supportDialogRef.current) {
                      supportDialogRef.current.setFormData({
                        issue_type: 'CHAMPIONS_PROGRAM',
                        message: 'I am interested to join the Champions Program. Please help me to understand the program.'
                      });
                    }
                  }, 100);
                }}
                sx={{
                  background: 'linear-gradient(45deg, #4caf50, #66bb6a)',
                  borderRadius: { xs: '16px', sm: '20px' },
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1, sm: 1.5 },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 'bold',
                  textTransform: 'none',
                  boxShadow: '0 4px 15px rgba(76, 175, 80, 0.4)',
                  minHeight: { xs: '40px', sm: '48px' },
                  width: { xs: 'auto', sm: 'auto' },
                  maxWidth: { xs: '120px', sm: 'none' },
                  '&:hover': {
                    background: 'linear-gradient(45deg, #43a047, #5cb85c)',
                    boxShadow: '0 6px 20px rgba(76, 175, 80, 0.6)',
                    transform: 'translateY(-2px)'
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                    boxShadow: '0 2px 10px rgba(76, 175, 80, 0.4)'
                  }
                }}
              >
                Start
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
            <MobilePricingTable
                plans={PRICING_PLANS[activeTab]}
                features={allFeatures}
                onChoose={handleChoosePlan}
                referralDiscount={referralDiscount}
              />
          </Box>
        )}

        {activeTab !== 'Champions' && (
          <Box sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
            <Card sx={{ ...sectionCardSx, backgroundColor: '#ffffff' }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Grid container spacing={2}>
                  {trustSignals.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Grid item xs={12} sm={4} key={item.title}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'center', textAlign: 'center', py: 1 }}>
                          <Box sx={{ width: 40, height: 40, display: 'grid', placeItems: 'center', borderRadius: 3, backgroundColor: '#eef2ff', color: '#2563eb' }}>
                            <Icon fontSize="small" />
                          </Box>
                          <Typography sx={{ color: '#334155', fontWeight: 600 }}>{item.title}</Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Add-ons Section */}
        {activeTab !== 'Champions' && (
          <Box 
            mb={{ xs: 4, sm: 6, md: 8 }}
            sx={{ ...sectionCardSx, p: { xs: 3, sm: 4, md: 5 } }}
          >
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, mb: 4 }}>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mr: { xs: 0, sm: 2 }, mb: { xs: 1, sm: 0 }, color: '#0f172a' }}>
                Optional Add-ons
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, color: '#64748b' }}>
                (Available for subscribed users)
              </Typography>
            </Box>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: { xs: 2, sm: 3 }
            }}>
              {ADD_ONS[activeTab].map((addOn, index) => (
                <Box key={index} sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  p: { xs: 3, sm: 4 },
                  borderRadius: 4,
                  border: '1px solid rgba(148, 163, 184, 0.18)',
                  background: '#f8fafc',
                  boxShadow: '0 12px 32px rgba(15, 23, 42, 0.05)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    background: '#ffffff',
                    boxShadow: '0 18px 40px rgba(15, 23, 42, 0.09)',
                    borderColor: 'rgba(99, 102, 241, 0.2)'
                  }
                }}>
                  <Box sx={{ flex: 1, mr: { xs: 0, sm: 2 }, mb: { xs: 2, sm: 0 } }}>
                    <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 1, color: '#0f172a' }}>
                      {addOn.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, color: '#64748b' }}>
                      {addOn.description}
                    </Typography>
                  </Box>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    disabled
                    sx={{
                      borderColor: 'rgba(102, 126, 234, 0.3)',
                      color: 'text.secondary',
                      borderRadius: '20px',
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      width: { xs: '100%', sm: 'auto' }
                    }}
                  >
                    {addOn.buttonText}
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Champions Benefits Section */}
        {activeTab === 'Champions' && (
          <Box 
            mb={{ xs: 4, sm: 6, md: 8 }}
            sx={{
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(20px)',
              borderRadius: 4,
              p: { xs: 3, sm: 4, md: 6 },
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 4px 20px rgba(255, 255, 255, 0.15)'
            }}
          >
            <Typography 
              variant="h5" 
              component="h2" 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold', 
                mb: 4,
                background: 'linear-gradient(45deg, #4caf50, #66bb6a)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textAlign: 'center'
              }}
            >
              🎯 Champion Benefits
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: { xs: 2, sm: 3 }
            }}>
              {ADD_ONS[activeTab].map((addOn, index) => (
                <Box key={index} sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  p: { xs: 3, sm: 4 },
                  borderRadius: 3,
                  border: '1px solid rgba(76, 175, 80, 0.2)',
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(15px)',
                  boxShadow: '0 4px 20px rgba(76, 175, 80, 0.1)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    boxShadow: '0 12px 30px rgba(76, 175, 80, 0.2)',
                    borderColor: 'rgba(76, 175, 80, 0.3)'
                  }
                }}>
                  <Box sx={{ flex: 1, mr: { xs: 0, sm: 2 }, mb: { xs: 2, sm: 0 } }}>
                    <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1, color: 'success.main' }}>
                      {addOn.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {addOn.description}
                    </Typography>
                  </Box>

                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ ...sectionCardSx, p: { xs: 3, sm: 4, md: 5 }, mb: { xs: 4, sm: 6, md: 8 } }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 4, textAlign: 'center', color: '#0f172a', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
            Frequently Asked Questions
          </Typography>
          <Box sx={{ maxWidth: 820, mx: 'auto' }}>
            {faqItems.map((faq) => (
              <Accordion key={faq.question} disableGutters elevation={0} sx={{ mb: 2, borderRadius: '20px !important', overflow: 'hidden', border: '1px solid rgba(226,232,240,0.9)', boxShadow: 'none', '&::before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: { xs: 2, sm: 3 }, py: 0.5 }}>
                  <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>{faq.question}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pt: 0, pb: 2.5 }}>
                  <Typography sx={{ color: '#64748b', lineHeight: 1.7 }}>{faq.answer}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>

        <Box sx={{ py: { xs: 7, md: 9 }, background: 'linear-gradient(135deg, #0f172a 0%, #172554 55%, #1d4ed8 100%)', color: '#ffffff', position: 'relative', overflow: 'hidden', borderRadius: 6 }}>
          <Box sx={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at top left, rgba(148,163,184,0.18) 0%, rgba(148,163,184,0) 32%), radial-gradient(circle at bottom right, rgba(34,211,238,0.22) 0%, rgba(34,211,238,0) 28%)' }} />
          <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 700, mb: 2 }}>
              Start improving today
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.82)', maxWidth: 560, mx: 'auto', mb: 3.5 }}>
              No setup needed. Get started in minutes.
            </Typography>
            <Button variant="contained" size="large" onClick={() => handleChoosePlan(PRICING_PLANS[activeTab][0]?.isRecommended ? PRICING_PLANS[activeTab][0].title : (PRICING_PLANS[activeTab].find((p) => p.isRecommended)?.title || PRICING_PLANS[activeTab][0].title))} sx={{ px: 4, py: 1.4, borderRadius: 999, fontWeight: 700, background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', boxShadow: '0 18px 40px rgba(79, 70, 229, 0.28)', '&:hover': { background: 'linear-gradient(135deg, #1d4ed8 0%, #6d28d9 100%)', transform: 'translateY(-2px)' } }}>
              Start Free Trial
            </Button>
          </Container>
        </Box>
      </Container>

      <Box
        component="footer"
        sx={{
          py: 2.5,
          px: 2,
          bgcolor: '#f8f9fa',
          borderTop: '1px solid #e5e7eb',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Typography
          component="p"
          sx={{
            m: 0,
            fontSize: '0.8125rem',
            color: '#6b7280',
            fontWeight: 400,
            letterSpacing: '0.01em',
          }}
        >
          {new Date().getFullYear()} ThinkStyne. All rights reserved.
        </Typography>
      </Box>

      <SupportDialogFixed 
        ref={supportDialogRef}
        open={supportOpen} 
        onClose={() => setSupportOpen(false)} 
        presetEmail={user?.email || ''} 
      />
    </Box>
  );
};

export default Pricing;
